import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutEdiRequiredField } from "./autogen/AutogenLayoutEdiRequiredField";
import { DropdownItem, LookupModelSearchEvent, DynamicDbDisplayValueSearchEvent, DropdownSelectionEvent, DataDisplayEvent, Checkbox, TableRowDisplayEvent, TableRow, DataSourceMode, Textbox, TableRowCreationEvent, DataSourceValidationEvent, ClickEvent, Image, LookupModelSelectionEvent, Button } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";

export class EdiRequiredField extends AutogenLayoutEdiRequiredField {
  public static readonly FIELD_RULE: string = "F";
  public static readonly REFNO_RULE: string = "R";
  public static readonly NOTE_RULE: string = "N";
  public static readonly OTHER_CHARGE_RULE: string = "O";
  public static readonly COMMITMENT_RULE: string = "C";
  public static readonly LOAD_ANALYZER_RULE: string = "L";
  public static readonly TRANSACTION_TYPE_990 = "990";
  public static readonly TRANSACTION_TYPE_204 = "204";
  public static readonly TRANSACTION_TYPE_210 = "210";
  public static readonly TRANSACTION_TYPE_214 = "214";
  private _transactionType: string = "";
  private _version: string = "";
  private _partner_id: string = "";
  private _dateTypeSelected: boolean = false;
  private _deletedRules: TableRow[] = [];
  private _parentNeedsSaving: ((needsSaving: boolean) => void) | null = null;
  private _isGroupRule: boolean = false;

  public get isGroupRule(): boolean {
    return this._isGroupRule;
  }
  public set isGroupRule(value: boolean) {
    this._isGroupRule = value;
  }

  public get parentNeedsSaving(): ((needsSaving: boolean) => void) | null {
    return this._parentNeedsSaving;
  }
  public set parentNeedsSaving(value: ((needsSaving: boolean) => void) | null) {
    this._parentNeedsSaving = value;
  }

  private readonly HELP_WINDOW_CSS = `
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          padding: 20px;
          line-height: 1.6;
          color: #333;
          max-width: 800px;
        }
        h1 {
          color: #2c5282;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 10px;
        }
        h2 {
          color: #2d3748;
          margin-top: 0;
        }
        h3 {
          color: #4a5568;
          margin-top: 25px;
          margin-bottom: 15px;
        }
        h4 {
          color: #2d3748;
          margin-bottom: 8px;
          margin-top: 15px;
        }
        code {
          background-color: #f7fafc;
          padding: 2px 6px;
          border-radius: 3px;
          font-family: 'Courier New', monospace;
          color: #e53e3e;
          font-weight: bold;
        }
        ul {
          margin: 10px 0;
          padding-left: 20px;
        }
        li {
          margin-bottom: 8px;
        }
        .example {
          background-color: #f8f9fa;
          border-left: 4px solid #4299e1;
          padding: 15px;
          margin: 15px 0;
          border-radius: 0 4px 4px 0;
        }
        .example h4 {
          margin-top: 0;
          color: #2b6cb0;
        }
        .example p {
          margin: 8px 0;
        }
        strong {
          color: #2d3748;
        }
        em {
          color: #718096;
          background-color: #edf2f7;
          padding: 8px;
          border-radius: 4px;
          display: block;
          margin: 15px 0;
        }
        .placeholder {
          color: #333;
        }
      `;

  override onLoad() {
    this.setReportRuleTypeItems();
    this.setTableNameDropDownItems();
    this.setWhichStopOptions();
    this.tableEdiDataRule.addRowCreateListener((event) => {
      // This fires when a new row is being added to the table
      const tableRow = event.getTableRow();

      // Override the deleteRow method for this specific row
      const originalDeleteRow = tableRow.deleteRow.bind(tableRow);

      tableRow.data.set("version", this.version);
      tableRow.data.set("transaction_type", this.transactionType);
      tableRow.data.set("element_id", "128");
      this.parentNeedsSaving?.(true);
      tableRow.deleteRow = () => {
        this.parentNeedsSaving?.(true);
        CommonDialogs.showYesNo("Are you sure you want to delete this record?", "Confirm Deletion").then(async yes => {
          if (yes) {
            try {
              await Api.delete("lme/datafusion/edi-data-rule", { id: tableRow.data.get("id") });
              this.tableEdiDataRule.deleteRow(tableRow.index);
            } catch (error) {
              CommonDialogs.showError("Error deleting record: " + error);
            }
          }
        });
      }
    });
  }

  setReportRuleTypeItems() {
    const ruleTypeConfig = [
      { value: EdiRequiredField.FIELD_RULE, text: "Field", excludeTransactions: [] },
      { value: EdiRequiredField.REFNO_RULE, text: "Reference Number", excludeTransactions: [] },
      { value: EdiRequiredField.NOTE_RULE, text: "Comment", excludeTransactions: [] },
      { value: EdiRequiredField.COMMITMENT_RULE, text: "Commitment", excludeTransactions: [EdiRequiredField.TRANSACTION_TYPE_214] },
      { value: EdiRequiredField.OTHER_CHARGE_RULE, text: "Other Charges", excludeTransactions: [EdiRequiredField.TRANSACTION_TYPE_990, EdiRequiredField.TRANSACTION_TYPE_214] }
    ];

    const dropdownItems = ruleTypeConfig
      .filter(rule => !rule.excludeTransactions.includes(this.transactionType))
      .map(rule => new DropdownItem(rule.value, rule.text));

    this.textboxRuleType.items = dropdownItems;
    if (dropdownItems.length > 0) {
      this.textboxRuleType.selectedItem = dropdownItems[0];
    }
  }


  private isValidTransactionType(type: string): boolean {
    return type === EdiRequiredField.TRANSACTION_TYPE_990
      || type === EdiRequiredField.TRANSACTION_TYPE_204
      || type === EdiRequiredField.TRANSACTION_TYPE_210;
  }

  setWhichStopOptions() {
    const dropdownItems = [];
    if (this.isValidTransactionType(this.transactionType)) {
      dropdownItems.push(new DropdownItem("AL", "All Stops"));
      dropdownItems.push(new DropdownItem("?S", "Any Stop"));
      dropdownItems.push(new DropdownItem("SH", "Shipper"));
      dropdownItems.push(new DropdownItem("CN", "Consignee"));
      dropdownItems.push(new DropdownItem("LC", "Stops @ Location"));
      dropdownItems.push(new DropdownItem("FD", "First Delivery"));
      dropdownItems.push(new DropdownItem("?P", "Any Pickup"));
      dropdownItems.push(new DropdownItem("?D", "Any Delivery"));
      dropdownItems.push(new DropdownItem("?V", "Any Via Point"));
      dropdownItems.push(new DropdownItem("AP", "All Pickups"));
      dropdownItems.push(new DropdownItem("AD", "All Deliveries"));
      dropdownItems.push(new DropdownItem("AV", "All Via Points"));
    } else if (this.transactionType === EdiRequiredField.TRANSACTION_TYPE_214) {
      dropdownItems.push(new DropdownItem("AL", "All Stops"));
      dropdownItems.push(new DropdownItem("?S", "Any Stop"));
      dropdownItems.push(new DropdownItem("SH", "Shipper"));
      dropdownItems.push(new DropdownItem("CN", "Consignee"));
      dropdownItems.push(new DropdownItem("LC", "Stops @ Location"));
      dropdownItems.push(new DropdownItem("SS", "Status' Stop"));
      dropdownItems.push(new DropdownItem("NS", "Next Stop"));
      dropdownItems.push(new DropdownItem("FD", "First Delivery"));
      dropdownItems.push(new DropdownItem("?P", "Any Pickup"));
      dropdownItems.push(new DropdownItem("?D", "Any Delivery"));
      dropdownItems.push(new DropdownItem("?V", "Any Via Point"));
      dropdownItems.push(new DropdownItem("AP", "All Pickups"));
      dropdownItems.push(new DropdownItem("AD", "All Deliveries"));
      dropdownItems.push(new DropdownItem("AV", "All Via Points"));
    }
    if (this.textboxStopsToCheck) {
      this.textboxStopsToCheck.items = dropdownItems;
      if (dropdownItems.length > 0) {
        this.textboxStopsToCheck.selectedItem = dropdownItems[0];
      }
    }
  }
  setTableNameDropDownItems() {
    const dropdownItems = [];
    if (this.transactionType === EdiRequiredField.TRANSACTION_TYPE_990) {
      dropdownItems.push(new DropdownItem("edi_order", "Load Tender"));
      dropdownItems.push(new DropdownItem("edi_stop", "Stop"));
    } else if (this.transactionType === EdiRequiredField.TRANSACTION_TYPE_204) {
      dropdownItems.push(new DropdownItem("edi_order", "Load Tender"));
      dropdownItems.push(new DropdownItem("edi_stop", "Stop"));
      dropdownItems.push(new DropdownItem("movement", "Movement"));
    } else if (this.transactionType === EdiRequiredField.TRANSACTION_TYPE_210) {
      dropdownItems.push(new DropdownItem("edi_billing", "EDI Bill"));
      dropdownItems.push(new DropdownItem("orders", "Order"));
      dropdownItems.push(new DropdownItem("stop", "Stop"));
    } else if (this.transactionType === EdiRequiredField.TRANSACTION_TYPE_214) {
      dropdownItems.push(new DropdownItem("edistatus", "Status"));
      dropdownItems.push(new DropdownItem("orders", "Order"));
      dropdownItems.push(new DropdownItem("stop", "Stop"));
      dropdownItems.push(new DropdownItem("movement", "Movement"));
    }
    if (this.textboxTableName) {
      this.textboxTableName.items = dropdownItems;
      if (dropdownItems.length > 0) {
        this.textboxTableName.selectedItem = dropdownItems[0];
      }
    }
  }

  public get transactionType(): string {
    return this._transactionType;
  }

  public set transactionType(value: string) {
    this._transactionType = value;
    this.setTableNameDropDownItems();
    this.setWhichStopOptions();
  }
  public get version(): string {
    return this._version;
  }
  public set version(value: string) {
    this._version = value;
  }

  public get partnerId(): string {
    return this._partner_id;
  }

  public set partnerId(value: string) {
    this._partner_id = value;
  }

  /** This is an event handler for the beforeLookupModelSearch event of textboxFieldName. */
  textboxFieldNameBeforeLookupModelSearch(event: LookupModelSearchEvent) {
    event.filter.table_name = this.textboxTableName.selectedItem?.value;
    event.filter.do_not_filter = true;
  }

  /** This is an event handler for the beforeDynamicDbDisplayValueSearch event of textboxDataValue. */
  textboxDataValueBeforeDynamicDbDisplayValueSearch(event: DynamicDbDisplayValueSearchEvent) {
    const ruleType = this.mainDataSource.activeRow?.get("rule_type");
    event.addAddlValue("rule_type", ruleType);
    event.addAddlValue("transaction_type", this.transactionType);
  }


  /** This is an event handler for the afterDropdownSelection event of textboxRuleType. */
  textboxRuleTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
    this.setComponentVisibility();
    this.tableEdiDataRule.clearRows();
    this.tableEdiDataRule.resetQuickAddRow();
    this.setTableNameDropDownItems();
  }
  setComponentVisibility() {
    const ruleType = this.mainDataSource.activeRow?.get("rule_type");
    const tableNameValue = this.textboxTableName.selectedItem?.value;
    const stopsToCheckValue = this.textboxStopsToCheck.selectedItem?.value;

    if (ruleType === EdiRequiredField.FIELD_RULE) {
      this.textboxTableName.visible = true;
      this.textboxFieldName.visible = true;
      this.textboxPassTest.visible = true;

      // Show textboxStopsToCheck if table name is "edi_stop"
      this.textboxStopsToCheck.visible = (tableNameValue === "edi_stop" || tableNameValue === "stop");

      // Show textboxLocation if stops to check is "LC" and table name is "edi_stop"
      this.textboxLocation.visible = ((tableNameValue === "edi_stop" || tableNameValue === "stop") && stopsToCheckValue === "LC");

      this.tableEdiDataRule.rows.forEach(row => {
        const checkboxCase = row.findComponentById("checkboxCaseSensitive") as Checkbox;
        if (checkboxCase) {
          checkboxCase.enabled = true;
          checkboxCase.required = false; // Remove asterisk
        }

      });
    } else if (ruleType == EdiRequiredField.REFNO_RULE || ruleType == EdiRequiredField.NOTE_RULE) {
      this.textboxTableName.visible = false;
      this.textboxStopsToCheck.visible = true;
      this.textboxFieldName.visible = false;
      this.textboxPassTest.visible = false;

      // Show textboxLocation if stops to check is "LC"
      this.textboxLocation.visible = (stopsToCheckValue === "LC");

      this.tableEdiDataRule.rows.forEach(row => {
        const checkboxCase = row.findComponentById("checkboxCaseSensitive") as Checkbox;
        if (checkboxCase) {
          checkboxCase.enabled = false;
          checkboxCase.required = false; // Remove asterisk
        }
      })
    } else if (ruleType == EdiRequiredField.COMMITMENT_RULE) {
      this.textboxFieldName.visible = false;
      this.textboxTableName.visible = false;
      this.textboxStopsToCheck.visible = false;
      this.textboxPassTest.visible = false;
      this.textboxLocation.visible = false;
      this.tableEdiDataRule.rows.forEach(row => {
        const checkboxCase = row.findComponentById("checkboxCaseSensitive") as Checkbox;
        if (checkboxCase) {
          checkboxCase.enabled = false;
          checkboxCase.required = false; // Remove asterisk
        }
      })
    } else if (ruleType == EdiRequiredField.OTHER_CHARGE_RULE) {
      this.textboxFieldName.visible = false;
      this.textboxTableName.visible = false;
      this.textboxStopsToCheck.visible = false;
      this.textboxPassTest.visible = false;
      this.textboxLocation.visible = false;
      this.tableEdiDataRule.rows.forEach(row => {
        const checkboxCase = row.findComponentById("checkboxCaseSensitive") as Checkbox;
        if (checkboxCase) {
          checkboxCase.enabled = false;
          checkboxCase.required = false; // Remove asterisk
        }
      })
    }
  }

  /** This is an event handler for the onDisplay event of sourceEdiRequiredField. */
  sourceEdiRequiredFieldOnDisplay(event: DataDisplayEvent) {
    if (this.mainDataSource.activeRow) {
      const dataRules = this.mainDataSource.activeRow.get("data_rules");
      if (dataRules && dataRules.length > 0) {
        this.sourceEdiDataRule.parentDataSource = null;
        const modelRows: ModelRow[] = [];
        dataRules.forEach((rule: any) => {
          const modelRow = new ModelRow("lme/datafusion/edi-data-rule");
          this.mapRuleDataToModelRow(modelRow, rule);
          modelRows.push(modelRow);
        });
        this.tableEdiDataRule.dataSource.setRowsAndMode(DataSourceMode.NONE, modelRows);
      }
    }
    this.setComponentVisibility();
    this.textboxRuleType.enabled = this.mainDataSource.mode == DataSourceMode.ADD;
  }

  /**
   * Maps rule data to a ModelRow, handling both direct properties and _data fallbacks
   * @param modelRow The ModelRow to populate
   * @param rule The rule data object
   */
  private mapRuleDataToModelRow(modelRow: ModelRow, rule: any): void {
    const fieldMappings = [
      { field: "id", value: rule.id ?? rule["_data"]?.id },
      { field: "company_id", value: rule.company_id ?? rule["_data"]?.company_id },
      { field: "version", value: rule.version ?? rule["_data"]?.version },
      { field: "transaction_type", value: this.transactionType ?? rule["_data"]?.transaction_type },
      { field: "element_id", value: "128" },
      { field: "data_value", value: rule.data_value ?? rule["_data"]?.data_value },
      { field: "error_message", value: rule.error_message ?? rule["_data"]?.error_message },
      { field: "num_of_days", value: rule.num_of_days ?? rule["_data"]?.num_of_days },
      { field: "operator", value: rule.operator ?? rule["_data"]?.operator },
      { field: "required_field_id", value: rule.required_field_id ?? rule["_data"]?.required_field_id },
      { field: "test_value", value: rule.test_value ?? rule["_data"]?.test_value },
      { field: "case_sensitive", value: rule.case_sensitive ?? rule["_data"]?.case_sensitive }
    ];

    fieldMappings.forEach(mapping => {
      modelRow.set(mapping.field, mapping.value);
    });

    const lookupModel = rule?._lookup_test_value ?? rule["_data"]?._lookup_test_value;
    if (lookupModel) {
      modelRow.setLookupModelData("test_value", lookupModel);
    }
  }
  /** This is an event handler for the onRowDisplay event of tableEdiDataRule. */
  tableEdiDataRuleOnRowDisplay(event: TableRowDisplayEvent) {
    const ruleType = this.mainDataSource.activeRow?.get("rule_type");
    const row = event.target as TableRow;
    const checkCaseSensitive = row.findComponentById("checkboxCaseSensitive") as Checkbox;
    const textboxTestValue = row.findComponentById("textboxTestValue") as Textbox;
    const imageDateHelp = row.findComponentById("imageDateHelp") as Image;
    const buttonTestDate = row.findComponentById("buttonTestDate") as Button;
    const textErrorMessage = row.findComponentById("textboxErrorMessage") as Textbox;
    if (textErrorMessage) {
      textErrorMessage.enabled = !this.isGroupRule;
    }
    const value = row.data.get("data_value");
    const operator = row.data.get("operator");

    if (checkCaseSensitive) {
      checkCaseSensitive.visible = (operator === "=" || operator === "<>" || operator === "SW" || operator === "EW" || operator === "CN");
      checkCaseSensitive.required = false; // Remove the asterisk by setting required to false
    }
    if (textboxTestValue) {
      textboxTestValue.required = false; // Remove the asterisk from Test Value field
    }
    this.configureLookupModel(textboxTestValue, value);
    if (imageDateHelp) {
      imageDateHelp.visible = this._dateTypeSelected;
    }
    if (buttonTestDate) {
      buttonTestDate.visible = this._dateTypeSelected && (operator === "=" || operator === "<>" || operator === ">" || operator === ">=" || operator === "<=" || operator === "<");
    }
    this.configureComponentsForOperator(row, operator);
  }

  /** Configures lookup model properties for a textbox based on whether the value is RQ */
  private configureLookupModel(textbox: Textbox, value: string) {
    if (value === "RQ") {
      // Set lookup model properties for RQ values
      textbox.lookupModel = "lme/datafusion/edi-standard-code-lookup-raw";
      textbox.lookupModelAllowFreeform = true;
      textbox.lookupModelAllowSearchAll = true;
      textbox.lookupModelAllowShowAllResults = true;
      textbox.lookupModelDisplayField = "standard_code";
      textbox.lookupModelInputDelay = 300;
      textbox.lookupModelLayout = "lme/datafusion/EdiStandardCodeLookupRaw";
      textbox.lookupModelLayoutHeight = 500;
      textbox.lookupModelLayoutWidth = 400;
      textbox.lookupModelMinChars = 2;
      textbox.lookupModelResultField = "standard_code";
      textbox.lookupModelDisabled = false;
      textbox.addBeforeLookupModelSearchListener((e: LookupModelSearchEvent) => {
        e.filter.element_id = "128";
        e.filter.version = this.version;
        e.filter.partner_id = this.partnerId;
      });
    } else if (value === "CI") {
      // Set lookup model properties for RQ values
      textbox.lookupModel = "lme/dispatch/charge-code-lookup";
      textbox.lookupModelAllowFreeform = true;
      textbox.lookupModelAllowSearchAll = true;
      textbox.lookupModelAllowShowAllResults = true;
      textbox.lookupModelDisplayField = "descr";
      textbox.lookupModelInputDelay = 300;
      textbox.lookupModelLayout = "lme/dispatch/ChargeCodeLookup";
      textbox.lookupModelLayoutHeight = 500;
      textbox.lookupModelLayoutWidth = 400;
      textbox.lookupModelMinChars = 2;
      textbox.lookupModelResultField = "id";
      textbox.lookupModelDisabled = false;
      textbox.quickInfoLayout = "lme/dispatch/ChargeCodeQuickInfo";
    } else {
      // Remove lookup model properties for non-RQ values
      textbox.lookupModel = null;
      textbox.lookupModelAllowFreeform = true;
      textbox.lookupModelAllowSearchAll = false;
      textbox.lookupModelAllowShowAllResults = false;
      textbox.lookupModelDisplayField = null;
      textbox.lookupModelInputDelay = null;
      textbox.lookupModelLayout = null;
      textbox.lookupModelLayoutHeight = null;
      textbox.lookupModelLayoutWidth = null;
      textbox.lookupModelMinChars = null;
      textbox.lookupModelResultField = null;
      textbox.lookupModelDisabled = true;
    }
  }

  /** This is an event handler for the afterDropdownSelection event of textboxDataValue. */
  textboxDataValueAfterDropdownSelection(event: DropdownSelectionEvent) {
    const textbox = event.target as Textbox;
    const value = textbox.selectedItem.value;

    // Find the row that contains this textbox by traversing up the parent hierarchy
    const row = TableRow.getContainingTableRow(textbox) as TableRow;
    if (row) {
      const textTV = row.findComponentById("textboxTestValue") as Textbox;
      const textOp = row.findComponentById("textboxOperator") as Textbox;

      // Use the refactored method to configure lookup model
      this.configureLookupModel(textTV, value);

      if ("EX" === value) {
        textTV.setSelectedItemFromValue("SD");

        textOp.setSelectedItemFromValue("WI");
        textOp.enabled = false;
        textTV.enabled = false;
      } else if ("AC" === value) {
        textTV.setSelectedItemFromValue("AV");
        textOp.setSelectedItemFromValue("HM");
        textOp.enabled = false;
        textTV.enabled = false;
      } else {
        textOp.selectedItem = null;
      }
    }

  }

  /** This is an event handler for the afterDropdownSelection event of textboxOperator. */
  textboxOperatorAfterDropdownSelection(event: DropdownSelectionEvent) {
    const textbox = event.target as Textbox;
    const value = textbox.selectedItem?.value;

    // Find the textboxNumOfDays component in the current row
    const row = TableRow.getContainingTableRow(textbox) as TableRow;
    if (row) {
      this.configureComponentsForOperator(row, value);
      this.setRequiredForTestValue(row, value);
    }

  }

  /** Configures component visibility and behavior based on the selected operator value */
  private configureComponentsForOperator(row: TableRow, operatorValue: string) {
    const textboxNumOfDays = row.findComponentById("textboxNumOfDays") as Textbox;
    const checkCaseSensitive = row.findComponentById("checkboxCaseSensitive") as Checkbox;
    const imageMaskHelp = row.findComponentById("imageMaskHelp") as Image;
    const imageDateHelp = row.findComponentById("imageDateHelp") as Image;
    const buttonTestDate = row.findComponentById("buttonTestDate") as Button;
    const textTestValue = row.findComponentById("textboxTestValue") as Textbox;

    // Show textboxNumOfDays if value is "WI", otherwise hide it
    if (textboxNumOfDays) {
      textboxNumOfDays.visible = (operatorValue === "WI");
      textboxNumOfDays.required = (operatorValue === "WI");
    }

    if (checkCaseSensitive) {
      checkCaseSensitive.visible = (operatorValue === "=" || operatorValue === "<>" || operatorValue === "SW" || operatorValue === "EW" || operatorValue === "CN" || operatorValue === "MK");
    }

    if (textTestValue) {
      textTestValue.enabled = (operatorValue != "NN" && operatorValue != "IN");
    }

    if (imageMaskHelp) {
      if (operatorValue === "MK") {
        this._dateTypeSelected = false;
        imageMaskHelp.visible = true;
        imageDateHelp.visible = false;
        buttonTestDate.visible = false;
      } else {
        imageMaskHelp.visible = false;
      }
    }

    if (imageDateHelp) {
      buttonTestDate.visible = this._dateTypeSelected && (operatorValue === "=" || operatorValue === "<>" || operatorValue === ">" || operatorValue === ">=" || operatorValue === "<=" || operatorValue === "<");
    }

  }

  /** Configures component visibility and behavior based on the selected operator value */
  private setRequiredForTestValue(row: TableRow, operatorValue: string) {
    const testValueTextbox = row.findComponentById("textboxTestValue") as Textbox;
    const testDataValue = row.findComponentById("textboxDataValue") as Textbox;
    if (testValueTextbox) {
      if (testDataValue.selectedItem?.value !== "NT") {
        testValueTextbox.resetValidation();
        const testValues = new Set([
          "=", "<>", ">", ">=", "<", "<=",
          "SW", "EW", "CN", "LG", "MK", "HM", "WI"
        ]);
        testValueTextbox.required = testValues.has(operatorValue);
      } else {
        testValueTextbox.required = false;
      }
    }
  }

  /** This is an event handler for the onRowCreate event of tableEdiDataRule. */
  tableEdiDataRuleOnRowCreate(event: TableRowCreationEvent) {
    const row = event.target as TableRow;
    if (row) {
      row.data.set("version", this.version);
    }
    if (this.mainDataSource.activeRow?.get("rule_type") === EdiRequiredField.FIELD_RULE) {
      if (row) {
        row.data.set("data_value", "FV");

        // Set components as not required for new rows
        const checkCaseSensitive = row.findComponentById("checkboxCaseSensitive") as Checkbox;
        const textboxTestValue = row.findComponentById("textboxTestValue") as Textbox;
        if (checkCaseSensitive) {
          checkCaseSensitive.required = false;
        }
        if (textboxTestValue) {
          textboxTestValue.required = false;
        }
      }
    }
  }

  /** This is an event handler for the onValidate event of sourceEdiRequiredField. */
  sourceEdiRequiredFieldOnValidate(event: DataSourceValidationEvent) {
    const dataRuleCount = this.sourceEdiDataRule.rowCount;
    if (dataRuleCount === 0) {
      // Add a validation error to the results array but don't prevent here
      // The parent layout will handle the prevention
      event.validationResults.push({
        isValid: false,
        component: this,
        validationMessage: "At least one Data Rule is required."
      });
    }
  }

  /** This is an event handler for the onClick event of imageMaskHelp. */
  imageMaskHelpOnClick(event: ClickEvent) {
    // Calculate window dimensions: 50% width, 100% height
    const screenWidth = screen.width;
    const screenHeight = screen.height;
    const windowWidth = Math.floor(screenWidth * 0.5);
    const windowHeight = screenHeight;

    // Open a new window to display help text
    const helpWindow = window.open('', '_blank', `width=${windowWidth},height=${windowHeight},scrollbars=yes,resizable=yes`);
    if (helpWindow) {
      // Create the HTML structure using modern DOM methods
      const doc = helpWindow.document;

      // Set the title
      doc.title = 'Mask Help';

      // Create and add CSS styles
      const style = doc.createElement('style');
      style.textContent = this.HELP_WINDOW_CSS;
      doc.head.appendChild(style);

      // Create the main content
      const h1 = doc.createElement('h1');
      h1.textContent = 'Mask Help Information';
      doc.body.appendChild(h1);

      const placeholderDiv = doc.createElement('div');
      placeholderDiv.className = 'placeholder';

      const contentDiv = doc.createElement('div');

      const htmlContent = `
        <h2>Mask Pattern Guide</h2>
        <p><strong>A mask consists of special characters and literals:</strong></p>
        <ul>
          <li>A <code>'@'</code> in the mask string will match any alphabetic character.</li>
          <li>A <code>'#'</code> in the mask string will match any numeric character.</li>
          <li>A <code>'?'</code> in the mask string will match any character.</li>
          <li>Any other mask string character must be matched <strong>EXACTLY</strong>.</li>
        </ul>

        <p><em>The length of the value being tested is also considered. If the value being tested does not match the length of the mask provided, the test value cannot conform to the mask value.</em></p>

        <h3>Examples:</h3>

        <div class="example">
          <h4>Example #1: <code>###</code></h4>
          <p>This mask expects the value being tested to be <strong>3 characters</strong> in length, and include <strong>3 numeric characters</strong>.</p>
          <p>✓ A value of <code>'567'</code> would conform to this mask.</p>
        </div>

        <div class="example">
          <h4>Example #2: <code>@@@@</code></h4>
          <p>This mask expects the value being tested to be <strong>4 characters</strong> in length, and to include <strong>4 alphabetic characters</strong>.</p>
          <p>✓ A value of <code>'DEFG'</code> would conform to this mask.</p>
        </div>

        <div class="example">
          <h4>Example #3: <code>@@##</code></h4>
          <p>This mask expects the value being tested to be <strong>4 characters</strong> in length, begin with <strong>2 alphabetic characters</strong>, and end with <strong>2 numeric characters</strong>.</p>
          <p>✓ A value of <code>'RS78'</code> would conform to this mask.</p>
        </div>

        <div class="example">
          <h4>Example #4: <code>SI-####</code></h4>
          <p>This mask expects the value being tested to be <strong>7 characters</strong> in length, beginning with <code>'SI'</code>, followed by a <code>'-'</code>, and ending with <strong>4 numeric characters</strong>.</p>
          <p>✓ A value of <code>'SI-1234'</code> would conform to this mask.</p>
        </div>

        <div class="example">
          <h4>Example #5: <code>??##A</code></h4>
          <p>This mask expects the value being tested to be <strong>5 characters</strong> in length, beginning with <strong>any two characters</strong>, followed by <strong>2 numeric characters</strong>, and ending with an <code>'A'</code>.</p>
          <p>✓ The values <code>'X112A'</code> or <code>'5678A'</code> would conform to this mask.</p>
        </div>
      `;

      contentDiv.innerHTML = htmlContent;
      placeholderDiv.appendChild(contentDiv);

      doc.body.appendChild(placeholderDiv);

      helpWindow.focus();
    }
  }

  /** This is an event handler for the onLookupModelSelection event of textboxFieldName. */
  textboxFieldNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
    const row = event.selection as ModelRow;
    if (row) {
      const dataType = row.get("data_type");
      this._dateTypeSelected = dataType.includes("date");
    }

    // Loop through all existing rows in tableEdiDataRule and update imageDateHelp visibility
    this.tableEdiDataRule?.rows?.forEach(tableRow => {
      const imageDateHelp = tableRow.findComponentById("imageDateHelp") as Image;
      const imageMaskHelp = tableRow.findComponentById("imageMaskHelp") as Image;
      if (imageDateHelp) {
        if (!imageMaskHelp.visible) { //don't set date help to visible if mask is visible
          imageDateHelp.visible = this._dateTypeSelected;
        }
      }
    });

    // Also update the quick add row if it exists
    const quickAddRow = this.tableEdiDataRule.quickAddRow;
    if (quickAddRow) {
      const imageDateHelp = quickAddRow.findComponentById("imageDateHelp") as Image;
      if (imageDateHelp) {
        imageDateHelp.visible = this._dateTypeSelected;
      }
    }
  }

  /** This is an event handler for the onClick event of imageDateHelp. */
  imageDateHelpOnClick(event: ClickEvent) {
    // Calculate window dimensions: 50% width, 100% height
    const screenWidth = screen.width;
    const screenHeight = screen.height;
    const windowWidth = Math.floor(screenWidth * 0.5);
    const windowHeight = screenHeight;

    // Open a new window to display help text
    const helpWindow = window.open('', '_blank', `width=${windowWidth},height=${windowHeight},scrollbars=yes,resizable=yes`);
    if (helpWindow) {
      // Create the HTML structure using modern DOM methods
      const doc = helpWindow.document;

      // Set the title
      doc.title = 'Mask Help';

      // Create and add CSS styles
      const style = doc.createElement('style');
      style.textContent = this.HELP_WINDOW_CSS;
      doc.head.appendChild(style);

      // Create the main content
      const h1 = doc.createElement('h1');
      h1.textContent = 'Date Help Information';
      doc.body.appendChild(h1);

      const placeholderDiv = doc.createElement('div');
      placeholderDiv.className = 'placeholder';

      const contentDiv = doc.createElement('div');

      const htmlContent = `
<h2>Date Comparison Operators</h2>
  <p>
    When using the <code>=</code>, <code>&lt;&gt;</code>, <code>&lt;</code>, <code>&lt;=</code>,
    <code>&gt;</code>, or <code>&gt;=</code> operator, dates can be compared against either a
    relative or hard-coded value. For example, it may be necessary to test that a date is more
    than 2 days in the future, or is after the first of the next month. Several options are
    available to help accommodate these types of tests.
  </p>

  <h3>Hard-Coded Date Values</h3>
  <p>
    If the requirement is to always compare the date against a single date value, a date may be
    hard-coded into the test value field in the following format: <code>MM/DD/yyyy HHmm</code>
  </p>
  <p><strong>Example:</strong> <code>12/31/2017 0001</code></p>

  <h3>Keywords</h3>
  <p>If a hard-coded date will not suffice, the following keywords are available:</p>
  <ul>
    <li><code>NOW</code> – Current date/time</li>
    <li><code>TODAY</code> – Current date (without time)</li>
    <li><code>MONTHBEGIN</code> – First day of the current month (without time)</li>
    <li><code>MONTHEND</code> – Last day of the current month (without time)</li>
    <li><code>YEARBEGIN</code> – First day of the current year (without time)</li>
    <li><code>YEAREND</code> – Last day of the current year (without time)</li>
  </ul>

  <h3>Modifiers</h3>
  <p>These keywords can be combined with modifiers to adjust the date/time:</p>
  <ul>
    <li><code>M</code> – Add/subtract months</li>
    <li><code>D</code> – Add/subtract days</li>
    <li><code>H</code> – Add/subtract hours</li>
    <li><code>m</code> – Add/subtract minutes</li>
  </ul>

  <p>
    A hard-coded time can also be specified at the end of any keyword/modifier combination.
    If provided, that time will override any other time values.
  </p>

  <p>
    Keyword/Modifier values are enclosed in brackets <code>[]</code>.
    Modifiers can be combined with each other.
  </p>

  <h3>Important Notes</h3>
  <ul>
    <li>Dates produced by keywords will not include a time, unless:</li>
    <ul>
      <li>The <code>NOW</code> keyword is used</li>
      <li>A modifier alters the time</li>
      <li>A hard-coded time is supplied</li>
    </ul>
  </ul>

  <h3>Examples (assuming current date/time is 07/01/2018 @ 1400)</h3>

  <div class="example"><strong>Example #1:</strong> <code>[NOW]</code><br>
    Returns: <code>07/01/2018 1400</code> (current date/time).
  </div>

  <div class="example"><strong>Example #2:</strong> <code>[NOW+2H]</code><br>
    Returns: <code>07/01/2018 1600</code> (current date/time + 2 hours).
  </div>

  <div class="example"><strong>Example #3:</strong> <code>[NOW-1M]</code><br>
    Returns: <code>06/01/2018 1400</code> (current date/time - 1 month).
  </div>

  <div class="example"><strong>Example #4:</strong> <code>[TODAY]</code><br>
    Returns: <code>07/01/2018</code> (no time).
  </div>

  <div class="example"><strong>Example #5:</strong> <code>[TODAY+2D]</code><br>
    Returns: <code>07/03/2018</code> (current date + 2 days, no time).
  </div>

  <div class="example"><strong>Example #6:</strong> <code>[TODAY-2D]</code><br>
    Returns: <code>06/29/2018</code> (current date - 2 days, no time).
  </div>

  <div class="example"><strong>Example #7:</strong> <code>[TODAY+12H]</code><br>
    Returns: <code>07/02/2018 1200</code> (current date + 12 hours).
  </div>

  <div class="example"><strong>Example #8:</strong> <code>[TODAY-1M+2D]</code><br>
    Returns: <code>06/03/2018</code> (current date - 1 month + 2 days).
  </div>

  <div class="example"><strong>Example #9:</strong> <code>[MONTHBEGIN+1M]</code><br>
    Returns: <code>08/01/2018</code> (beginning of month + 1 month).
  </div>

  <div class="example"><strong>Example #10:</strong> <code>[YEAREND-1D]</code><br>
    Returns: <code>12/30/2018</code> (end of year - 1 day).
  </div>

  <div class="example"><strong>Example #11:</strong> <code>[TODAY 1700]</code><br>
    Returns: <code>07/01/2018 1700</code> (time supplied explicitly).
  </div>

  <div class="example"><strong>Example #12:</strong> <code>[TODAY+2H 1700]</code><br>
    Returns: <code>07/01/2018 1700</code> (hard-coded time overrides modifier).
  </div>

  <div class="example"><strong>Example #13:</strong> <code>12/31/2018 1235</code><br>
    Returns: <code>12/31/2018 1235</code> (hard-coded date/time).
  </div>`;

      contentDiv.innerHTML = htmlContent;
      placeholderDiv.appendChild(contentDiv);

      doc.body.appendChild(placeholderDiv);

      helpWindow.focus();


    }
  }

  async dateHelpButtonOnClick(event: ClickEvent) {
    const button = event.target as Button;
    const tableRow = TableRow.getContainingTableRow(button) as TableRow;
    if (tableRow) {
      const textTV = tableRow.findComponentById("textboxTestValue") as Textbox;
      if (textTV) {
        try {
          if (!StringUtil.isEmptyString(textTV.text)) {
            const response = await Api.post("lme/datafusion/test-supplied-date", { date_string: textTV.text });
            if (response && response.data && response.data.length > 0) {
              const result = response.data[0].parsed_date;
              this.showDateTestResultDialog(textTV.text, result);
            }
          } else {
            this.showDateTestResultDialog(textTV.text, 'No test string supplied');
          }
        } catch (error) {
          console.error('Error testing date string:', error);
          this.showDateTestResultDialog(textTV.text, 'Error parsing date');
        }
      }
    }
  }

  /**
   * Displays the date test result in an HTML dialog
   */
  private showDateTestResultDialog(suppliedDate: string, parsedDate: string) {
    const now = new Date().toLocaleString();

    const panelContents = `
      <table style="font-family: 'Segoe UI', Arial, sans-serif; border-collapse: collapse; width: 100%;">
        <tr>
          <td style="text-align: right; padding: 8px; font-weight: bold;">Current Date/Time:</td>
          <td style="padding: 8px;">${now}</td>
        </tr>
        <tr>
          <td style="text-align: right; padding: 8px; font-weight: bold;">Supplied Date/Time:</td>
          <td style="padding: 8px;">${suppliedDate}</td>
        </tr>
        <tr>
          <td style="text-align: right; padding: 8px; font-weight: bold;">Result:</td>
          <td style="padding: 8px;">${parsedDate}</td>
        </tr>
      </table>
    `;

    // Calculate window dimensions
    const screenWidth = screen.width;
    const screenHeight = screen.height;
    const windowWidth = Math.floor(screenWidth * 0.4);
    const windowHeight = Math.floor(screenHeight * 0.3);

    // Open a new window to display the result
    const resultWindow = window.open('', '_blank',
      `width=${windowWidth},height=${windowHeight},scrollbars=no,resizable=yes,toolbar=no,menubar=no`);

    if (resultWindow) {
      const doc = resultWindow.document;
      doc.title = 'Date Test Result';

      // Add CSS styles
      const style = doc.createElement('style');
      style.textContent = `
        body {
          font-family: 'Segoe UI', Arial, sans-serif;
          margin: 20px;
          background-color: #f5f5f5;
        }
        h1 {
          color: #2c5282;
          font-size: 18px;
          margin-bottom: 20px;
          text-align: center;
        }
        table {
          background-color: white;
          border: 1px solid #ddd;
          border-radius: 4px;
          width: 100%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        td {
          border-bottom: 1px solid #eee;
        }
        tr:last-child td {
          border-bottom: none;
        }
      `;
      doc.head.appendChild(style);

      // Create content
      const h1 = doc.createElement('h1');
      h1.textContent = 'Date Test Result';
      doc.body.appendChild(h1);

      const contentDiv = doc.createElement('div');
      contentDiv.innerHTML = panelContents;
      doc.body.appendChild(contentDiv);

      resultWindow.focus();
    }
  }

  /** This is an event handler for the afterDropdownSelection event of textboxStopsToCheck. */
  textboxStopsToCheckAfterDropdownSelection(event: DropdownSelectionEvent) {
    this.setComponentVisibility();
  }

  /** This is an event handler for the afterDropdownSelection event of textboxTableName. */
  textboxTableNameAfterDropdownSelection(event: DropdownSelectionEvent) {
    this.setComponentVisibility();
  }

  /** This is an event handler for the beforeDynamicDbDisplayValueSearch event of textboxTestValue. */
  textboxTestValueBeforeDynamicDbDisplayValueSearch(event: DynamicDbDisplayValueSearchEvent) {
    const ruleType = this.mainDataSource.activeRow?.get("rule_type");
    event.addAddlValue("rule_type", ruleType);
    event.addAddlValue("transaction_type", this.transactionType);
  }
}
